import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Layout from "../../Component/Layout";
import { PURCHASE_API } from "../../apiConfig";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    FaChartLine,
    FaUsers,
    FaWallet,
    FaBoxOpen,
    FaCalendarAlt,
    FaSyncAlt,
    FaArrowRight,
} from "react-icons/fa";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
} from "recharts";

// ==========================================
// SHARED STYLE HELPERS
// ==========================================
const card = {
    background: "#fff",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,.05),0 2px 4px -1px rgba(0,0,0,.03)",
    padding: "20px 22px",
};

const cardHeader = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "14px",
};

const iconBadge = (bg, color) => ({
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    background: bg,
    color: color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
});

const viewReportLink = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3b82f6",
    marginTop: "14px",
    cursor: "pointer",
    textDecoration: "none",
};


function ViewReport({ to }) {
    const navigate = useNavigate();
    return (
        <div
            style={viewReportLink}
            onClick={to ? () => navigate(to) : undefined}
            role={to ? "link" : undefined}
        >
            View Report <FaArrowRight size={11} />
        </div>
    );
}

function DonutLegend({ data }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "16px" }}>
            {data.map((d) => (
                <div
                    key={d.name}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "13px",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span
                            style={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                background: d.color,
                                display: "inline-block",
                            }}
                        />
                        <span style={{ color: "#334155", fontWeight: 500 }}>{d.name}</span>
                    </div>
                    <span style={{ color: "#0f172a", fontWeight: 700 }}>
                        {d.formatted ? formatAmountDisplay(d.formatted).replace("₹ ", "") : `${d.value} Cr`}{" "}
                        <span style={{ color: "#94a3b8", fontWeight: 500 }}>({d.pct}%)</span>
                    </span>
                </div>
            ))}
        </div>
    );
}

function DonutChart({ data, centerValue, centerLabel }) {
    return (
        <div style={{ position: "relative", width: "180px", height: "180px", margin: "0 auto" }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                        stroke="none"
                    >
                        {data.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip formatter={(val) => `₹${val} Cr`} />
                </PieChart>
            </ResponsiveContainer>
            <div
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                }}
            >
                <div style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a" }}>
                    {centerValue}
                </div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>{centerLabel}</div>
            </div>
        </div>
    );
}

function RankedList({ title, data }) {
    return (
        <div style={{ marginTop: "18px" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#334155", marginBottom: "10px" }}>
                {title}
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "11px",
                    color: "#94a3b8",
                    marginBottom: "6px",
                }}
            >
                <span>Name</span>
                <span>Value (₹ Cr)</span>
            </div>
            {data.map((d) => (
                <div
                    key={d.name}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "13px",
                        padding: "6px 0",
                        borderBottom: "1px solid #f1f5f9",
                    }}
                >
                    <span style={{ color: "#334155" }}>{d.name}</span>
                    <span style={{ color: "#0f172a", fontWeight: 600 }}>{d.value}</span>
                </div>
            ))}
        </div>
    );
}

// Returns first and last day of the current month, as JS Date objects.
function getCurrentMonthRange() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // last day of month
    return { start, end };
}

// Formats a Date -> Date range as "Aug 1 – Aug 31, 2026"
function formatRangeLabel(start, end) {
    if (!start || !end) return "Select date range";
    const startStr = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const endStr = end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${startStr} – ${endStr}`;
}

// Format a JS Date as "YYYY-MM-DD" for the API (local date, not UTC-shifted)
function toApiDateString(date) {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

// Turns { value, unit } (from the API) into "₹ 128.7 Cr" style text.
function formatAmountDisplay(formatted) {
    if (!formatted) return "₹ 0";
    const { value, unit } = formatted;
    return unit ? `₹ ${value} ${unit}` : `₹ ${value}`;
}

// Turns a deltaPct/deltaPp number into "▲ 14.6% vs Apr 2025" style text.
// Returns null (render nothing) when there's no comparable previous period.
function formatDeltaText(delta, suffix, unit = "%") {
    if (delta === null || delta === undefined) return null;
    const arrow = delta >= 0 ? "▲" : "▼";
    const sign = delta >= 0 ? "" : "-";
    return `${arrow} ${sign}${Math.abs(delta)}${unit} ${suffix}`;
}

// Label for the comparison period, e.g. "vs Jul 2026"
function formatPrevPeriodLabel(prevPeriod) {
    if (!prevPeriod?.start) return "vs previous period";
    const d = new Date(prevPeriod.start);
    return `vs ${d.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`;
}

function CFODashboard() {
    const [refreshing, setRefreshing] = useState(false);
    const { start: defaultStart, end: defaultEnd } = getCurrentMonthRange();
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef(null);

    // ---- Sales & Profitability (KPI card 1) ----
    const [salesProfitability, setSalesProfitability] = useState(null);
    const [salesProfitabilityLoading, setSalesProfitabilityLoading] = useState(true);
    const [salesProfitabilityError, setSalesProfitabilityError] = useState(null);

    // ---- Sales & Profitability Trend (chart) ----
    const [salesTrendData, setSalesTrendData] = useState([]);
    const [salesTrendLoading, setSalesTrendLoading] = useState(true);
    const [salesTrendError, setSalesTrendError] = useState(null);

    // ---- Receivables (KPI card 2 + Ageing chart) ----
    const [receivables, setReceivables] = useState(null);
    const [receivablesLoading, setReceivablesLoading] = useState(true);
    const [receivablesError, setReceivablesError] = useState(null);

    // ---- Payables (KPI card 3 + Summary donut + Top Suppliers) ----
    const [payables, setPayables] = useState(null);
    const [payablesLoading, setPayablesLoading] = useState(true);
    const [payablesError, setPayablesError] = useState(null);

    // ---- Inventory (KPI card 4 + Summary donut) ----
    const [inventory, setInventory] = useState(null);
    const [inventoryLoading, setInventoryLoading] = useState(true);
    const [inventoryError, setInventoryError] = useState(null);

    // Close the picker when clicking outside of it
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetches Sales & Profitability card data for the selected date range
    // GET ${PURCHASE_API}/cfo-dashboard/sales-profitability?startDate=&endDate=
    const fetchSalesProfitability = async () => {
        if (!startDate || !endDate) return;
        setSalesProfitabilityLoading(true);
        setSalesProfitabilityError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/cfo-dashboard/sales-profitability`, {
                params: {
                    startDate: toApiDateString(startDate),
                    endDate: toApiDateString(endDate),
                },
            });
            if (!data.success) throw new Error(data.message || "Failed to load Sales & Profitability");
            setSalesProfitability(data);
        } catch (err) {
            console.error("Sales & Profitability fetch error:", err);
            setSalesProfitabilityError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setSalesProfitabilityLoading(false);
        }
    };

    // Fetches the Sales & Profitability Trend chart data for the selected date range
    // GET ${PURCHASE_API}/cfo-dashboard/sales-profitability/trend?startDate=&endDate=&months=5
    const fetchSalesTrend = async () => {
        if (!startDate || !endDate) return;
        setSalesTrendLoading(true);
        setSalesTrendError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/cfo-dashboard/sales-profitability/trend`, {
                params: {
                    startDate: toApiDateString(startDate),
                    endDate: toApiDateString(endDate),
                    months: 5,
                },
            });
            if (!data.success) throw new Error(data.message || "Failed to load trend");
            setSalesTrendData(data.trend || []);
        } catch (err) {
            console.error("Sales Trend fetch error:", err);
            setSalesTrendError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setSalesTrendLoading(false);
        }
    };

    // Fetches Receivables card + ageing chart data for the selected date range
    // GET ${PURCHASE_API}/cfo-dashboard/receivables?startDate=&endDate=
    const fetchReceivables = async () => {
        if (!startDate || !endDate) return;
        setReceivablesLoading(true);
        setReceivablesError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/cfo-dashboard/receivables`, {
                params: {
                    startDate: toApiDateString(startDate),
                    endDate: toApiDateString(endDate),
                },
            });
            if (!data.success) throw new Error(data.message || "Failed to load Receivables");
            setReceivables(data);
        } catch (err) {
            console.error("Receivables fetch error:", err);
            setReceivablesError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setReceivablesLoading(false);
        }
    };

    // Fetches Payables card + summary donut + top suppliers for the selected date range
    // GET ${PURCHASE_API}/cfo-dashboard/payables?startDate=&endDate=
    const fetchPayables = async () => {
        if (!startDate || !endDate) return;
        setPayablesLoading(true);
        setPayablesError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/cfo-dashboard/payables`, {
                params: {
                    startDate: toApiDateString(startDate),
                    endDate: toApiDateString(endDate),
                },
            });
            if (!data.success) throw new Error(data.message || "Failed to load Payables");
            setPayables(data);
        } catch (err) {
            console.error("Payables fetch error:", err);
            setPayablesError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setPayablesLoading(false);
        }
    };

    // Fetches Inventory card + summary donut for the selected date range
    // GET ${PURCHASE_API}/cfo-dashboard/inventory?startDate=&endDate=
    const fetchInventory = async () => {
        if (!startDate || !endDate) return;
        setInventoryLoading(true);
        setInventoryError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/cfo-dashboard/inventory`, {
                params: {
                    startDate: toApiDateString(startDate),
                    endDate: toApiDateString(endDate),
                },
            });
            if (!data.success) throw new Error(data.message || "Failed to load Inventory");
            setInventory(data);
        } catch (err) {
            console.error("Inventory fetch error:", err);
            setInventoryError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setInventoryLoading(false);
        }
    };

    // Refetch whenever the selected date range changes
    useEffect(() => {
        fetchSalesProfitability();
        fetchSalesTrend();
        fetchReceivables();
        fetchPayables();
        fetchInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        if (start && end) {
            setShowPicker(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([fetchSalesProfitability(), fetchSalesTrend(), fetchReceivables(), fetchPayables(), fetchInventory()]);
        setRefreshing(false);
    };

    return (
        <Layout defaultSidebarOpen={false}>
            <div
                style={{
                    padding: "24px 32px",
                    backgroundColor: "#f8fafc",
                    minHeight: "calc(100vh - 60px)",
                }}
            >
                {/* ================= HEADER ================= */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "16px",
                        marginBottom: "28px",
                    }}
                >
                    <div>
                        <h2
                            style={{
                                margin: "0 0 6px 0",
                                color: "#0f172a",
                                fontWeight: 700,
                                fontSize: "26px",
                            }}
                        >
                            CFO Dashboard
                        </h2>
                        <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
                            At-a-glance financial overview
                        </p>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ position: "relative" }} ref={pickerRef}>
                            <div
                                onClick={() => setShowPicker((prev) => !prev)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    background: "#fff",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "10px",
                                    padding: "9px 14px",
                                    fontSize: "13px",
                                    color: "#334155",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                }}
                            >
                                <FaCalendarAlt size={13} color="#64748b" />
                                {formatRangeLabel(startDate, endDate)}
                            </div>

                            {showPicker && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "calc(100% + 8px)",
                                        right: 0,
                                        zIndex: 100,
                                        background: "#fff",
                                        borderRadius: "10px",
                                        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                                        border: "1px solid #e2e8f0",
                                        padding: "10px",
                                    }}
                                >
                                    <DatePicker
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={handleDateChange}
                                        inline
                                        monthsShown={2}
                                    />
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: "8px",
                                            marginTop: "8px",
                                        }}
                                    >
                                        <button
                                            onClick={() => {
                                                const { start, end } = getCurrentMonthRange();
                                                setStartDate(start);
                                                setEndDate(end);
                                                setShowPicker(false);
                                            }}
                                            style={{
                                                background: "transparent",
                                                border: "1px solid #e2e8f0",
                                                borderRadius: "8px",
                                                padding: "6px 12px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#334155",
                                                cursor: "pointer",
                                            }}
                                        >
                                            This Month
                                        </button>
                                        <button
                                            onClick={() => setShowPicker(false)}
                                            style={{
                                                background: "#2563eb",
                                                border: "none",
                                                borderRadius: "8px",
                                                padding: "6px 14px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#fff",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={handleRefresh}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "#2563eb",
                                border: "none",
                                borderRadius: "10px",
                                padding: "10px 16px",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            <FaSyncAlt
                                size={13}
                                style={{
                                    animation: refreshing ? "spin 0.8s linear infinite" : "none",
                                }}
                            />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* ================= KPI CARDS ================= */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                        gap: "20px",
                        marginBottom: "24px",
                    }}
                >
                    <div style={card}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#eff6ff", "#3b82f6")}>
                                <FaChartLine size={16} />
                            </div>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>
                                1. Sales &amp; Profitability
                            </span>
                        </div>

                        {salesProfitabilityLoading ? (
                            <div style={{ fontSize: "13px", color: "#94a3b8", padding: "10px 0" }}>Loading…</div>
                        ) : salesProfitabilityError ? (
                            <div style={{ fontSize: "12px", color: "#ef4444", padding: "10px 0" }}>
                                {salesProfitabilityError}
                            </div>
                        ) : (
                            <>
                                <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                                    {formatAmountDisplay(salesProfitability?.sales?.formatted)}
                                </div>
                                <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "10px" }}>
                                    Sales this Period
                                </div>
                                {(() => {
                                    const text = formatDeltaText(
                                        salesProfitability?.sales?.deltaPct,
                                        formatPrevPeriodLabel(salesProfitability?.previousPeriod)
                                    );
                                    return text ? (
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: (salesProfitability?.sales?.deltaPct ?? 0) >= 0 ? "#16a34a" : "#ef4444",
                                                marginBottom: "16px",
                                            }}
                                        >
                                            {text}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "16px" }}>
                                            No prior period data
                                        </div>
                                    );
                                })()}

                                <div style={{ display: "flex", gap: "20px", borderTop: "1px solid #f1f5f9", paddingTop: "14px" }}>
                                    <div>
                                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>
                                            {formatAmountDisplay(salesProfitability?.grossProfit?.formatted)}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#64748b" }}>Gross Profit</div>
                                        {(() => {
                                            const text = formatDeltaText(
                                                salesProfitability?.grossProfit?.deltaPct,
                                                formatPrevPeriodLabel(salesProfitability?.previousPeriod)
                                            );
                                            return (
                                                <div
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: 600,
                                                        color: (salesProfitability?.grossProfit?.deltaPct ?? 0) >= 0 ? "#16a34a" : "#ef4444",
                                                    }}
                                                >
                                                    {text || "—"}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>
                                            {salesProfitability?.grossMarginPct?.raw ?? 0}%
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#64748b" }}>Gross Margin %</div>
                                        {(() => {
                                            const text = formatDeltaText(
                                                salesProfitability?.grossMarginPct?.deltaPp,
                                                formatPrevPeriodLabel(salesProfitability?.previousPeriod),
                                                " pp"
                                            );
                                            return (
                                                <div
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: 600,
                                                        color: (salesProfitability?.grossMarginPct?.deltaPp ?? 0) >= 0 ? "#16a34a" : "#ef4444",
                                                    }}
                                                >
                                                    {text || "—"}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                        <ViewReport />
                    </div>

                    <div style={card}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#f0fdf4", "#22c55e")}>
                                <FaUsers size={16} />
                            </div>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>
                                2. Receivables
                            </span>
                        </div>

                        {receivablesLoading ? (
                            <div style={{ fontSize: "13px", color: "#94a3b8", padding: "10px 0" }}>Loading…</div>
                        ) : receivablesError ? (
                            <div style={{ fontSize: "12px", color: "#ef4444", padding: "10px 0" }}>
                                {receivablesError}
                            </div>
                        ) : (
                            <>
                                <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                                    {formatAmountDisplay(receivables?.total?.formatted)}
                                </div>
                                <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "10px" }}>
                                    Total Outstanding
                                </div>
                                {(() => {
                                    const text = formatDeltaText(
                                        receivables?.total?.deltaPct,
                                        formatPrevPeriodLabel(receivables?.previousPeriod)
                                    );
                                    return text ? (
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: (receivables?.total?.deltaPct ?? 0) >= 0 ? "#16a34a" : "#ef4444",
                                                marginBottom: "16px",
                                            }}
                                        >
                                            {text}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "16px" }}>
                                            No prior period data
                                        </div>
                                    );
                                })()}

                                <div style={{ display: "flex", gap: "16px", borderTop: "1px solid #f1f5f9", paddingTop: "14px", flexWrap: "wrap" }}>
                                    {(receivables?.buckets || []).map((b) => (
                                        <div key={b.name}>
                                            <div style={{ fontSize: "13px", fontWeight: 700, color: "#0f172a" }}>
                                                {formatAmountDisplay(b.formatted)}
                                            </div>
                                            <div style={{ fontSize: "11px", color: "#64748b" }}>{b.name}</div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        <ViewReport to="/finance/dashboard/receivables-dashboard" />
                    </div>

                    <div style={card}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#fff7ed", "#f97316")}>
                                <FaWallet size={16} />
                            </div>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>
                                3. Payables
                            </span>
                        </div>
                        {payablesLoading ? (
                            <div style={{ fontSize: "13px", color: "#94a3b8", padding: "10px 0" }}>Loading…</div>
                        ) : payablesError ? (
                            <div style={{ fontSize: "12px", color: "#ef4444", padding: "10px 0" }}>
                                {payablesError}
                            </div>
                        ) : (
                            <>
                                <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                                    {formatAmountDisplay(payables?.total?.formatted)}
                                </div>
                                <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "10px" }}>
                                    Total Payable
                                </div>
                                {(() => {
                                    const text = formatDeltaText(
                                        payables?.total?.deltaPct,
                                        formatPrevPeriodLabel(payables?.previousPeriod)
                                    );
                                    return text ? (
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: (payables?.total?.deltaPct ?? 0) >= 0 ? "#16a34a" : "#ef4444",
                                                marginBottom: "16px",
                                            }}
                                        >
                                            {text}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "16px" }}>
                                            No prior period data
                                        </div>
                                    );
                                })()}

                                <div style={{ display: "flex", gap: "24px", borderTop: "1px solid #f1f5f9", paddingTop: "14px" }}>
                                    <div>
                                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>
                                            {formatAmountDisplay(payables?.dueThisMonth?.formatted)}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#64748b" }}>Due This Month</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>
                                            {formatAmountDisplay(payables?.overdue?.formatted)}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#64748b" }}>Overdue</div>
                                    </div>
                                </div>
                            </>
                        )}
                        <ViewReport to="/finance/dashboard/payables-dashboard" />
                    </div>

                    <div style={card}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#f5f3ff", "#8b5cf6")}>
                                <FaBoxOpen size={16} />
                            </div>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>
                                4. Inventory
                            </span>
                        </div>
                        {inventoryLoading ? (
                            <div style={{ fontSize: "13px", color: "#94a3b8", padding: "10px 0" }}>Loading…</div>
                        ) : inventoryError ? (
                            <div style={{ fontSize: "12px", color: "#ef4444", padding: "10px 0" }}>
                                {inventoryError}
                            </div>
                        ) : (
                            <>
                                <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                                    {formatAmountDisplay(inventory?.total?.formatted)}
                                </div>
                                <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "10px" }}>
                                    Total Inventory Value
                                </div>
                                {(() => {
                                    const text = formatDeltaText(
                                        inventory?.total?.deltaPct,
                                        formatPrevPeriodLabel(inventory?.previousPeriod)
                                    );
                                    return text ? (
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: (inventory?.total?.deltaPct ?? 0) >= 0 ? "#16a34a" : "#ef4444",
                                                marginBottom: "16px",
                                            }}
                                        >
                                            {text}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "16px" }}>
                                            No prior period data
                                        </div>
                                    );
                                })()}

                                <div style={{ display: "flex", flexDirection: "column", gap: "6px", borderTop: "1px solid #f1f5f9", paddingTop: "14px" }}>
                                    {(inventory?.buckets || []).map((d) => (
                                        <div key={d.name} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                                            <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "#334155" }}>
                                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, display: "inline-block" }} />
                                                {d.name}
                                            </span>
                                            <span style={{ fontWeight: 600, color: "#0f172a" }}>
                                                {d.valueCr} Cr ({d.pct}%)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        <ViewReport to="/cable/inventory/inventory" />
                    </div>
                </div>

                {/* ================= CHARTS ROW ================= */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: "20px",
                    }}
                >
                    {/* Sales & Profitability Trend */}
                    <div style={card}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "10px" }}>
                            1. Sales &amp; Profitability Trend
                        </div>
                        {salesTrendLoading ? (
                            <div style={{ fontSize: "13px", color: "#94a3b8", padding: "30px 0", textAlign: "center" }}>
                                Loading trend…
                            </div>
                        ) : salesTrendError ? (
                            <div style={{ fontSize: "12px", color: "#ef4444", padding: "20px 0", textAlign: "center" }}>
                                {salesTrendError}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={200}>
                                <ComposedChart data={salesTrendData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                                    <Tooltip />
                                    <Bar dataKey="sales" name="Sales (₹ Cr)" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={22} />
                                    <Bar dataKey="grossProfit" name="Gross Profit (₹ Cr)" fill="#22c55e" radius={[4, 4, 0, 0]} barSize={22} />
                                    <Line type="monotone" dataKey="margin" name="Gross Margin (%)" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}

                        {!salesTrendLoading && !salesTrendError && salesTrendData.length >= 2 && (() => {
                            const latest = salesTrendData[salesTrendData.length - 1];
                            const prior = salesTrendData[salesTrendData.length - 2];
                            const pctChange = (curr, prev) =>
                                prev ? `${curr - prev >= 0 ? "▲" : "▼"} ${Math.abs((((curr - prev) / prev) * 100).toFixed(1))}%` : "—";
                            const ppChange = (curr, prev) =>
                                prev !== undefined ? `${curr - prev >= 0 ? "▲" : "▼"} ${Math.abs((curr - prev).toFixed(1))} pp` : "—";

                            return (
                                <div style={{ marginTop: "14px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#94a3b8", marginBottom: "6px" }}>
                                        <span>Metric</span>
                                        <span>{latest.month}</span>
                                        <span>{prior.month}</span>
                                        <span>Change</span>
                                    </div>
                                    {[
                                        ["Sales (₹ Cr)", latest.sales, prior.sales, pctChange(latest.sales, prior.sales)],
                                        ["Gross Profit (₹ Cr)", latest.grossProfit, prior.grossProfit, pctChange(latest.grossProfit, prior.grossProfit)],
                                        ["Gross Margin (%)", latest.margin, prior.margin, ppChange(latest.margin, prior.margin)],
                                    ].map((row) => (
                                        <div key={row[0]} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", padding: "5px 0", borderBottom: "1px solid #f1f5f9" }}>
                                            <span style={{ color: "#334155", flex: 2 }}>{row[0]}</span>
                                            <span style={{ color: "#0f172a", flex: 1, textAlign: "center" }}>{row[1]}</span>
                                            <span style={{ color: "#0f172a", flex: 1, textAlign: "center" }}>{row[2]}</span>
                                            <span style={{ color: "#16a34a", fontWeight: 600, flex: 1, textAlign: "right" }}>{row[3]}</span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                        <ViewReport />
                    </div>

                    {/* Receivables Ageing */}
                    <div style={card}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "10px" }}>
                            2. Receivables Ageing
                        </div>
                        {receivablesLoading ? (
                            <div style={{ fontSize: "13px", color: "#94a3b8", padding: "30px 0", textAlign: "center" }}>
                                Loading…
                            </div>
                        ) : receivablesError ? (
                            <div style={{ fontSize: "12px", color: "#ef4444", padding: "20px 0", textAlign: "center" }}>
                                {receivablesError}
                            </div>
                        ) : (
                            <>
                                <DonutChart
                                    data={(receivables?.buckets || []).map((b) => ({
                                        name: b.name,
                                        value: b.raw,
                                        color: b.color,
                                    }))}
                                    centerValue={`${receivables?.total?.formatted?.value ?? 0}${
                                        receivables?.total?.formatted?.unit ? " " + receivables.total.formatted.unit : ""
                                    }`}
                                    centerLabel="Total Outstanding"
                                />
                                <DonutLegend data={receivables?.buckets || []} />
                            </>
                        )}
                        <ViewReport to="/finance/dashboard/receivables-dashboard" />
                    </div>

                    {/* Payables Summary */}
                    <div style={card}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "10px" }}>
                            3. Payables Summary (₹ Cr)
                        </div>
                        {payablesLoading ? (
                            <div style={{ fontSize: "13px", color: "#94a3b8", padding: "30px 0", textAlign: "center" }}>
                                Loading…
                            </div>
                        ) : payablesError ? (
                            <div style={{ fontSize: "12px", color: "#ef4444", padding: "20px 0", textAlign: "center" }}>
                                {payablesError}
                            </div>
                        ) : (
                            <>
                                <DonutChart
                                    data={(payables?.buckets || []).map((b) => ({
                                        name: b.name,
                                        value: b.raw,
                                        color: b.color,
                                    }))}
                                    centerValue={`${payables?.total?.formatted?.value ?? 0}${
                                        payables?.total?.formatted?.unit ? " " + payables.total.formatted.unit : ""
                                    }`}
                                    centerLabel="Total Payable"
                                />
                                <DonutLegend data={payables?.buckets || []} />
                                <RankedList
                                    title="Top 5 Suppliers by Payable"
                                    data={(payables?.topSuppliers || []).map((s) => ({
                                        name: s.name,
                                        value: s.valueCr,
                                    }))}
                                />
                            </>
                        )}
                        <ViewReport to="/finance/dashboard/payables-dashboard" />
                    </div>

                    {/* Inventory Overview */}
                    <div style={card}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "10px" }}>
                            4. Inventory Overview (₹ Cr)
                        </div>
                        {inventoryLoading ? (
                            <div style={{ fontSize: "13px", color: "#94a3b8", padding: "30px 0", textAlign: "center" }}>
                                Loading…
                            </div>
                        ) : inventoryError ? (
                            <div style={{ fontSize: "12px", color: "#ef4444", padding: "20px 0", textAlign: "center" }}>
                                {inventoryError}
                            </div>
                        ) : (
                            <>
                                <DonutChart
                                    data={(inventory?.buckets || []).map((b) => ({
                                        name: b.name,
                                        value: b.raw,
                                        color: b.color,
                                    }))}
                                    centerValue={`${inventory?.total?.formatted?.value ?? 0}${
                                        inventory?.total?.formatted?.unit ? " " + inventory.total.formatted.unit : ""
                                    }`}
                                    centerLabel="Total Inventory Value"
                                />
                                <DonutLegend data={inventory?.buckets || []} />
                            </>
                        )}
                        <ViewReport to="/cable/inventory/inventory" />
                    </div>
                </div>

                {/* <div style={{ textAlign: "center", fontSize: "12px", color: "#94a3b8", marginTop: "24px" }}>
                    All amounts are in ₹ Crore &nbsp;|&nbsp; Data is real-time and synchronized across all plants
                </div> */}

                <style>{`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </Layout>
    );
}

export default CFODashboard;